/**
 * PROTOTYPE ONLY — not production UI.
 *
 * Floating bar for flipping between UI prototype variants on a single route. The
 * current variant lives in the `?variant=` search param so a URL is shareable and
 * survives a reload. Hidden in the production environment (REACT_APP_APP_ENV), so it
 * still shows on staging.
 */
import {useCallback, useEffect} from "react";
import {useSearchParams} from "react-router-dom";

export interface PrototypeVariant {
  key: string;
  name: string;
}

interface PrototypeSwitcherProps {
  variants: PrototypeVariant[];
  current: string;
  /** Search param the variant is stored under. Defaults to `variant`. */
  param?: string;
}

export function PrototypeSwitcher({
  variants,
  current,
  param = "variant",
}: PrototypeSwitcherProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const index = Math.max(
    0,
    variants.findIndex((variant) => variant.key === current)
  );

  const cycle = useCallback(
    (step: number) => {
      const next = variants[(index + step + variants.length) % variants.length];
      const params = new URLSearchParams(searchParams);
      params.set(param, next.key);
      setSearchParams(params, {replace: true});
    },
    [index, param, searchParams, setSearchParams, variants]
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }

      // Don't steal the arrow keys from anything the user is typing into.
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }

      cycle(event.key === "ArrowLeft" ? -1 : 1);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cycle]);

  if (process.env.REACT_APP_APP_ENV === "production") {
    return null;
  }

  const variant = variants[index];

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: "#111827",
        color: "#fff",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        fontFamily: "monospace",
        fontSize: 13,
      }}
    >
      <button
        type="button"
        onClick={() => cycle(-1)}
        style={arrowStyle}
        aria-label="Previous variant"
      >
        ←
      </button>
      <span style={{padding: "0 12px", whiteSpace: "nowrap"}}>
        {variant.key} ({variant.name})
      </span>
      <button
        type="button"
        onClick={() => cycle(1)}
        style={arrowStyle}
        aria-label="Next variant"
      >
        →
      </button>
    </div>
  );
}

const arrowStyle: React.CSSProperties = {
  cursor: "pointer",
  border: "none",
  background: "rgba(255,255,255,0.12)",
  color: "#fff",
  width: 32,
  height: 32,
  borderRadius: 999,
  fontSize: 15,
  lineHeight: 1,
};
